import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.height || 'auto'};
  background-color: #f0f0f0;
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => props['data-loaded'] ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
  will-change: opacity;
`;

const BlurPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  filter: blur(10px);
  transform: scale(1.1);
  opacity: ${props => props['data-loaded'] ? 0 : 1};
  transition: opacity 0.3s ease-in-out;
  will-change: opacity, transform;
`;

// WebP support detection - memoize the result
const supportsWebP = (() => {
  const canvas = document.createElement('canvas');
  if (canvas.getContext && canvas.getContext('2d')) {
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }
  return false;
})();

// Cache for WebP conversions
const webpCache = new Map();

const OptimizedImage = memo(({ src, alt, height, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [webpSrc, setWebpSrc] = useState('');
  const convertingRef = useRef(false);
  const mountedRef = useRef(true);

  const convertToWebP = useCallback(async () => {
    if (!supportsWebP || convertingRef.current) {
      setWebpSrc(src);
      return;
    }

    // Check cache first
    if (webpCache.has(src)) {
      setWebpSrc(webpCache.get(src));
      return;
    }

    convertingRef.current = true;

    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const webpBlob = await convertToWebPFormat(blob);
      const webpUrl = URL.createObjectURL(webpBlob);
      
      // Cache the result
      webpCache.set(src, webpUrl);
      
      if (mountedRef.current) {
        setWebpSrc(webpUrl);
      }
    } catch (error) {
      console.error('Error converting to WebP:', error);
      if (mountedRef.current) {
        setWebpSrc(src);
      }
    } finally {
      convertingRef.current = false;
    }
  }, [src]);

  useEffect(() => {
    mountedRef.current = true;
    convertToWebP();

    return () => {
      mountedRef.current = false;
    };
  }, [convertToWebP]);

  const handleLoad = useCallback(() => {
    if (mountedRef.current) {
      setIsLoaded(true);
    }
  }, []);

  return (
    <ImageContainer height={height} className={className}>
      <BlurPlaceholder src={src} data-loaded={isLoaded} />
      <StyledImage
        src={webpSrc || src}
        alt={alt}
        loading="lazy"
        onLoad={handleLoad}
        data-loaded={isLoaded}
      />
    </ImageContainer>
  );
});

// Helper function to convert image to WebP
const convertToWebPFormat = async (blob) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (webpBlob) => {
          resolve(webpBlob);
        },
        'image/webp',
        0.8
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
};

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage; 